import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type CreatedShareToken, type ShareTokenView, api } from "../lib/api";
import { AdminAlert } from "./AdminAlert";

export interface ShareAppPanelProps {
  appId: string;
  appName: string;
}

/** #123: TTL-Presets in Minuten (Backend erlaubt 5..43200). */
const TTL_PRESETS: ReadonlyArray<{ value: number; key: string }> = [
  { value: 60, key: "admin.share.ttl.1h" },
  { value: 1440, key: "admin.share.ttl.24h" },
  { value: 10080, key: "admin.share.ttl.7d" },
  { value: 43200, key: "admin.share.ttl.30d" },
];

/**
 * #123/#6: Panel zum Verwalten von Gast-Share-Links einer App. Listet bestehende
 * Token, erlaubt das Erzeugen (mit einmalig sichtbarer URL) und Widerrufen.
 *
 * Seit #6 inline als Tab unter den App-Einstellungen gerendert (kein Modal mehr).
 * Die Token-Endpunkte sind nur im Showroom-Modus gemountet; auf Entra-Hubs
 * liefern sie 404 — das wird als `admin.share.unavailable` graceful behandelt.
 */
export function ShareAppPanel(props: ShareAppPanelProps): JSX.Element {
  const { t, i18n } = useTranslation();

  const [tokens, setTokens] = useState<ShareTokenView[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [ttlMinutes, setTtlMinutes] = useState<number>(1440);
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<CreatedShareToken | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.shareTokens.list(props.appId);
      setTokens(res.tokens);
      setUnavailable(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setUnavailable(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  };

  // Token-Liste beim Öffnen laden.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `load` wird bewusst nur bei App-Wechsel ausgelöst, nicht bei jedem Render.
  useEffect(() => {
    void load();
  }, [props.appId]);

  const fmtDate = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString(i18n.language) : t("admin.share.never");

  const onCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    setCopied(false);
    try {
      const body: { ttl_minutes: number; label?: string } = { ttl_minutes: ttlMinutes };
      const trimmed = label.trim();
      if (trimmed) body.label = trimmed;
      const result = await api.admin.shareTokens.create(props.appId, body);
      setCreated(result);
      setLabel("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (tokenId: string): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.admin.shareTokens.revoke(props.appId, tokenId);
      setNotice(t("admin.share.revoked"));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
    } catch {
      /* Clipboard kann blockiert sein; URL bleibt sichtbar zum manuellen Kopieren. */
    }
  };

  if (unavailable) {
    return <p className="launchpad__hint">{t("admin.share.unavailable")}</p>;
  }

  return (
    <div className="admin__share-panel">
      <p className="launchpad__hint">{t("admin.share.intro")}</p>
      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
      {notice ? <AdminAlert kind="success">{notice}</AdminAlert> : null}

      {created ? (
        <div className="admin__share-created">
          <p>
            <strong>{t("admin.share.createdHeading")}</strong>
          </p>
          <p className="launchpad__hint launchpad__hint--error">{t("admin.share.onceWarning")}</p>
          <div className="admin__share-url">
            <code>{created.url}</code>
            <button type="button" onClick={() => void onCopy()}>
              {copied ? t("admin.share.copied") : t("admin.share.copy")}
            </button>
          </div>
        </div>
      ) : null}

      <h4>{t("admin.share.createHeading")}</h4>
      <form className="admin__form" onSubmit={(e) => void onCreate(e)}>
        <div className="admin__field">
          <label htmlFor="share_ttl">{t("admin.share.ttlLabel")}</label>
          <select
            id="share_ttl"
            value={ttlMinutes}
            onChange={(e) => setTtlMinutes(Number.parseInt(e.target.value, 10))}
          >
            {TTL_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {t(p.key)}
              </option>
            ))}
          </select>
        </div>
        <div className="admin__field">
          <label htmlFor="share_label">{t("admin.share.labelLabel")}</label>
          <input
            id="share_label"
            value={label}
            placeholder={t("admin.share.labelPlaceholder")}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        {busy ? (
          <output className="admin__busy" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            {t("common.saving")}
          </output>
        ) : null}
        <div className="admin__form-actions">
          <button type="submit" className="primary" disabled={busy}>
            {t("admin.share.createButton")}
          </button>
        </div>
      </form>

      <h4>{t("admin.share.listHeading")}</h4>
      {loading ? (
        <p className="launchpad__hint">{t("admin.loading")}</p>
      ) : tokens.length === 0 ? (
        <p className="launchpad__hint">{t("admin.share.empty")}</p>
      ) : (
        <ul className="admin__share-list">
          {tokens.map((token) => (
            <li key={token.id} className="admin__share-item">
              <div className="admin__share-item-main">
                <span className="admin__share-item-label">
                  {token.label ?? t("admin.share.noLabel")}
                </span>
                <span className={`admin__share-status admin__share-status--${token.status}`}>
                  {t(`admin.share.status.${token.status}`)}
                </span>
              </div>
              <small>
                {t("admin.share.expiresAt", { date: fmtDate(token.expires_at) })}
                {" · "}
                {t("admin.share.lastUsed", { date: fmtDate(token.last_used_at) })}
              </small>
              {token.status === "active" ? (
                <button
                  type="button"
                  className="admin__danger"
                  disabled={busy}
                  onClick={() => void onRevoke(token.id)}
                >
                  {t("admin.share.revoke")}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
